import { zodResolver } from '@hookform/resolvers/zod'
import { FC, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { useCheckAuth } from '@/features/auth/check'

import { useUser } from '@/entities/user'

import { WEB_CONSEST_LINK } from '@/shared/lib/utils/constants/pdfDocs'
import { normalizeEmail } from '@/shared/lib/validation/email'
import { formatPhoneMasked } from '@/shared/lib/validation/phone'
import { Button, Input, RHFEmailInput, RHFPhoneInput } from '@/shared/ui'
import Flex from '@/shared/ui/Flex'

import { ContactData, contactSchema } from './data'

import styles from './styles.module.scss'

export type { ContactData }

interface ContactFormProps {
	onSubmit: (values: ContactData) => Promise<void>
	loading?: boolean
	withApplyMessage?: boolean
	direction?: 'vertical' | 'horizontal'
	userAutocomplete?: boolean
}

export const ContactForm: FC<ContactFormProps> = ({
	onSubmit,
	loading,
	withApplyMessage,
	direction = 'horizontal',
	userAutocomplete = false
}) => {
	const {
		control,
		handleSubmit,
		setValue,
		formState: { errors }
	} = useForm<ContactData>({
		mode: 'onBlur',
		resolver: zodResolver(contactSchema),
		defaultValues: {
			NAME: '',
			PHONE: '',
			EMAIL: ''
		}
	})

	const { isAuthorized } = useCheckAuth()
	const { user, isLoading: userInfoIsLoading } = useUser()

	useEffect(() => {
		if (userAutocomplete && isAuthorized && user) {
			const { last_name = ``, first_name = ``, user_phone } = user

			setValue('NAME', [first_name, last_name].join(` `).trim())
			setValue('PHONE', user_phone ? formatPhoneMasked(user_phone) : ``)

			if (user.user_email && user.user_email !== '0') {
				setValue('EMAIL', normalizeEmail(user.user_email))
			}
		}
	}, [setValue, isAuthorized, user, userAutocomplete])

	const onFinish = (values: ContactData) => {
		onSubmit(values)
	}

	return (
		<form
			noValidate
			className={`${styles.contactForm} ${direction === 'vertical' ? styles.vertical : ''}`}
			onSubmit={handleSubmit(onFinish)}>
			<Flex columnGap={16} column={direction === 'vertical'} row={direction === 'horizontal'}>
				<div className={styles.formField}>
					<Controller
						name="NAME"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								type="name"
								label="Имя"
								isLoading={userInfoIsLoading}
								className={errors.NAME ? styles.inputError : ''}
							/>
						)}
					/>
					{errors.NAME && <span className={styles.errorMessage}>{errors.NAME.message}</span>}
				</div>

				<div className={styles.formField}>
					<RHFPhoneInput
						name="PHONE"
						control={control}
						error={errors.PHONE}
						isLoading={userInfoIsLoading}
						className={errors.PHONE ? styles.inputError : ''}
					/>
				</div>

				<div className={styles.formField}>
					<RHFEmailInput
						name="EMAIL"
						control={control}
						error={errors.EMAIL}
						isLoading={userInfoIsLoading}
						className={errors.EMAIL ? styles.inputError : ''}
					/>
				</div>

				<Button
					className={styles.submitButton}
					theme="primary"
					isLoading={loading}
					disabled={loading}
					type="submit"
					id="gtm-prereservation-push">
					{!loading ? `Отправить заявку` : `Отправка`}
				</Button>
			</Flex>
			{withApplyMessage && (
				<p className={styles.docsMessage}>
					Нажимая на кнопку, я соглашаюсь на{' '}
					<a href={WEB_CONSEST_LINK} target="_blank">
						обработку персональных данных
					</a>
				</p>
			)}
		</form>
	)
}
